import { useMemo } from 'react';
import { WizardStep, IAppointmentSelection } from '../models';
import { getAvailableSlotsForDate } from '../constants';

interface IWizardValidation {
  isStepValid: boolean;
  isStepComplete: boolean;
  validationMessage: string;
  canNavigateForward: boolean;
  canNavigateBack: boolean;
}

export const useWizardValidation = (
  currentStep: WizardStep,
  stepIndex: number,
  totalSteps: number,
  selection: IAppointmentSelection,
  showValidationMessages: boolean = false
): IWizardValidation => {
  
  return useMemo(() => {
    let isStepValid = false;
    let isStepComplete = false;
    let validationMessage = '';
    let canNavigateForward = false;
    let canNavigateBack = stepIndex > 0;

    switch (currentStep) {
      case 'start':
        isStepValid = true;
        isStepComplete = true;
        canNavigateForward = true;
        break;

      case 'services':
        isStepValid = !!selection.service;
        isStepComplete = isStepValid;
        canNavigateForward = isStepValid;
        // Don't show validation message until user tries to proceed
        // The button being disabled is enough indication
        break;

      case 'employees':
        isStepValid = !!selection.employee;
        isStepComplete = isStepValid;
        canNavigateForward = isStepValid;
        // Don't show validation message until user tries to proceed
        break;

      case 'datetime':
        const hasDate = !!selection.date;
        const hasTime = !!selection.time;
        
        // Only show validation messages if explicitly requested (after user tries to proceed)
        if (showValidationMessages) {
          if (!hasDate) {
            validationMessage = 'Моля, изберете дата за да продължите';
          } else if (!hasTime) {
            // Check if selected date has available slots
            const availableSlots = selection.date ? getAvailableSlotsForDate(selection.date) : [];
            const hasAvailableSlots = availableSlots.some(slot => slot.isAvailable);
            
            if (!hasAvailableSlots) {
              validationMessage = 'Няма налични часове за избраната дата';
            } else {
              validationMessage = 'Моля, изберете час за да продължите';
            }
          }
        }
        
        isStepValid = hasDate && hasTime;
        isStepComplete = isStepValid;
        canNavigateForward = isStepValid;
        break;

      case 'login':
        // Login step is valid - navigation will be handled by the login component
        isStepValid = true;
        isStepComplete = true;
        canNavigateForward = true;
        break;

      case 'confirmation':
        const hasAllRequired = !!(selection.service && selection.employee && selection.date && selection.time);
        isStepValid = hasAllRequired;
        isStepComplete = hasAllRequired;
        canNavigateForward = hasAllRequired;
        
        if (!hasAllRequired) {
          validationMessage = 'Моля, завършете всички стъпки за да потвърдите резервацията';
        }
        break;

      case 'success':
        isStepValid = true;
        isStepComplete = true;
        canNavigateForward = false; // No next step from success
        canNavigateBack = false; // Don't allow going back from success
        break;

      default:
        break;
    }

    return {
      isStepValid,
      isStepComplete,
      validationMessage,
      canNavigateForward,
      canNavigateBack,
    };
  }, [currentStep, stepIndex, totalSteps, selection, showValidationMessages]);
};